// Import components for creating type aliases
import type { ComponentType, ReactNode } from 'react';

import type { components } from './api';

/**
 * Minimal context type for route guards that require authentication and roles.
 * Matches AuthState in useAuthStatus.ts.
 */
export interface RouteAuthContext {
  authentication: {
    status: 'AUTHENTICATED' | 'UNAUTHENTICATED' | 'PENDING';
    user: { assumed_roles: Record<string, string[] | string> } | null;
  };
}

// Re-export the components interface and other API types
export type { components } from './api';
export * from './errors';

/**
 * Helper type to access API schemas more concisely
 * Usage: Schema<'OrgPublic'> instead of components['schemas']['OrgPublic']
 *
 * Note: Some API types use namespaced names (e.g., 'app__api__v1__studies__SortOrder')
 * to avoid conflicts between different API endpoints that have similar types.
 */
type Schema<T extends keyof components['schemas']> = components['schemas'][T];

// ============================================================================
// SAML Types

export type SAMLMetadata = Schema<'SAMLMetadata'>;
export type SAMLProviderGetResponse = Schema<'SAMLProviderGetResponse'>;

/**
 * Flat form defaults for SAML provider configuration UI
 */
export type SAMLProviderFormDefaults = {
  provider_name: string;
  email_domain: string;
  metadata_url?: string | null;
  metadata_file?: string | null;
  attr_email: string;
  attr_first_name: string;
  attr_last_name: string;
  attr_roles: string;
};

// ============================================================================
// Organization Types
// ============================================================================

export type Contact = Schema<'Contact'>;
export type Org = Schema<'OrgPublic'>;
export type OrgPublic = Schema<'OrgPublic'>;
export type OrgCreate = Schema<'OrgCreate'>;
export type OrgUpdate = Schema<'OrgUpdate'>;

/**
 * Organization type used for table display.
 * Alias of Org (OrgPublic) used by the Organizations table.
 */
export type OrgTableRow = Org;

// ============================================================================
// Role Types
// ============================================================================

/**
 * Role-related types and constants
 */
export type InternalRole = Schema<'Role'>;
export type AssumeRoleRequest = Schema<'AssumeRoleRequest'>;
export type AssumeRoleResponse = Schema<'AssumeRoleResponse'>;

/** Wire values (lower_snake), not display strings — render them via `ROLE_LABELS` in `@/lib/roles`.
 *  Note the super-user value is `super_user`, not `super_admin`, while the field
 *  `UserPublic.is_super_admin` kept its original name. */
export const ROLES = ['super_user', 'org_admin', 'data_owner'] as const;
export const SUPER_ADMIN_ROLE = ROLES[0];
export const ORG_ADMIN_ROLE = ROLES[1];
export const DATA_OWNER_ROLE = ROLES[2];
export const BASIC_ROLES = [ORG_ADMIN_ROLE, DATA_OWNER_ROLE] as const;
export type Role = (typeof ROLES)[number];
// ============================================================================
// User Types
// ============================================================================

/**
 * User type with API Role
 */
export type User = Schema<'CurrentUser'>;

export type UserPublic = Schema<'UserPublic'>;
/**
 * Frontend-owned, NOT generated — and NOT part of the por-308 delta.
 *
 * `UserStudyPublic` is absent from `origin/main`'s spec too, and
 * `GET /users/{user_id}/studies` 404s live, so the frontend has been carrying a type
 * the backend already stopped emitting (backend handoff §3, "Class A"). Shape preserved
 * verbatim from the last spec that had it so the UserDetails studies table keeps
 * compiling; it cannot load data until an endpoint exists. Investigate against `main`
 * separately — do not treat this as schema-branch fallout.
 */
export type UserStudyPublic = {
  id: string;
  org_id: string;
  org_name: string;
  name: string;
  contact?: Contact | null;
  permissions: string[];
  created_at: string;
  created_by: string;
  created_by_full_name: string;
  updated_at?: string | null;
  updated_by?: string | null;
  updated_by_full_name?: string | null;
};
/**
 * One request model now serves both `/users/search` and `/orgs/{org_id}/users/search`. The
 * previous `__1`/`__2` split was an artifact of two same-named Pydantic classes, disambiguated
 * only by declaration order — so the old global-vs-org distinction was never real.
 * `UserSearchRequestGlobal` is kept as an alias so existing call sites keep reading naturally.
 */
export type UserSearchRequest = Schema<'UserSearchRequest'>;
export type UserSearchRequestGlobal = UserSearchRequest;
export type PaginatedUsersResponse = Schema<'PaginatedUsersResponse'>;
export type UserSortField = Schema<'UserSortField'>;
export type UserFilters = Schema<'UserFilters'>;
export type GrantUserOrgAccessRequest = Schema<'GrantUserOrgAccessRequest'>;
export type GrantUserOrgAccessResponse = Schema<'GrantUserOrgAccessResponse'>;
export type RevokeUserOrgAccessRequest = Schema<'RevokeUserOrgAccessRequest'>;
export type RevokeUserOrgAccessResponse = Schema<'RevokeUserOrgAccessResponse'>;

// ============================================================================
// Study Types
// ============================================================================

/**
 * StudyPublicGetOne with additional fields that were removed from the generated schema
 * but are still returned by the API and used in the UI.
 */
export type StudyPublicGetOne = Schema<'StudyPublicGetOne'> & {
  assays: StudyAssay[];
  num_sample_groups: number;
};
export type Study = StudyPublicGetOne;
export type StudyPublicGetMany = Schema<'StudyPublicGetMany'>;
export type StudyAssay = Schema<'StudyAssay'>;
export type PaginatedStudiesResponse = Schema<'PaginatedStudiesResponse'>;
export type StudyPermissions = Schema<'StudyPermissions'>;
export type StudyCreate = Schema<'StudyCreate'>;
export type StudyCreateResponse = Schema<'StudyCreateResponse'>;
// Override StudyUpdateResponse to use our augmented StudyPublicGetOne type
export type StudyUpdateResponse = { study: StudyPublicGetOne; message: string };
export type StudySearchRequest = Schema<'StudySearchRequest'>;
export type StudyFilters = Schema<'StudyFilters'>;
export type StudySortField = Schema<'StudySortField'>;
export type StudyAccessResponse = Schema<'StudyAccessResponse'>;
export type BatchGrantAccessRequest = Schema<'BatchGrantAccessRequest'>;
export type UserAccess = Schema<'UserAccess'>;

// The shared filter types were extracted backend-side, so the per-module
// `app__api__v1__<module>__*` duplicates are gone — there is exactly one of each now, and no
// mangled names remain in the spec.
export type SortOrder = Schema<'SortOrder'>;
export type TextFilterCondition = Schema<'TextFilterCondition'>;
export type TextFilterOperator = Schema<'TextFilterOperator'>;
export type DateFilterCondition = Schema<'DateFilterCondition'>;
export type DateFilterOperator = Schema<'DateFilterOperator'>;
export type LogicOperator = Schema<'LogicOperator'>;
/** The text-column container, renamed from the old per-module `FieldFilters`. */
export type TextFieldFilters = Schema<'TextFieldFilters'>;
/** @deprecated Use {@link TextFieldFilters}. */
export type FieldFilters = TextFieldFilters;
export type BooleanFieldFilters = Schema<'BooleanFieldFilters'>;
export type DateFieldFilters = Schema<'DateFieldFilters'>;
export type NumericFieldFilters = Schema<'NumericFieldFilters'>;
export type EqualityOperator = Schema<'EqualityOperator'>;

/**
 * Per-column filter containers. Every enum and boolean column now uses the same
 * `{ conditions, logic }` shape as text columns — `QCOutcomeSetFilter` and `WarningFilter`
 * are gone. Uuid columns need their own container: applying a text container to a uuid used
 * to 500 on every operator.
 */
export type QcOutcomeFieldFilters = Schema<'QcOutcomeFieldFilters'>;
export type SpeciesFieldFilters = Schema<'SpeciesFieldFilters'>;
export type SampleTypeFieldFilters = Schema<'SampleTypeFieldFilters'>;
export type InstrumentRunStatusFieldFilters = Schema<'InstrumentRunStatusFieldFilters'>;
export type UuidFieldFilters = Schema<'UuidFieldFilters'>;

// ============================================================================
// Instrument Types
// ============================================================================

export type InstrumentPublic = Schema<'InstrumentPublic'>;
export type InstrumentCreateRequest = Schema<'InstrumentCreateRequest'>;
export type InstrumentUpdateRequest = Schema<'InstrumentUpdateRequest'>;

// ============================================================================
// Assay Types (POR-322 catalog — backend `/assays/*` BFF endpoints)
//
// Now fully generated. These were hand-authored while `/assays/*` was undeployed
// and absent from the spec; codegen against por-308 produces all of them.
//
// Two shape differences from the old hand-written mirrors, both worth knowing:
//   - `ControlSample` marks `library_group`, `is_optional`, `expected_composition`
//     and `expected_proteoforms` OPTIONAL (the hand version required them).
//   - `AssayRunConfigControlSample.control_type` KEEPS its name. The
//     control_type -> sample_type rename applies to the *sample* DTOs only.
// ============================================================================

export type AssayType = Schema<'AssayType'>;
export type AssayPublicGetMany = Schema<'AssayPublicGetMany'>;
export type AssayPublicGetOne = Schema<'AssayPublicGetOne'>;
export type AssayRunConfig = Schema<'AssayRunConfig'>;
export type AssayRunConfigControlSample = Schema<'AssayRunConfigControlSample'>;
/**
 * Control-sample shape on the assay detail response.
 *
 * Three collapses happened here: `ControlSample` -> `ControlSamplePublic`,
 * `ControlSampleType` -> the shared `SampleType` (both `ControlSamplePublic.type` and
 * `AssayRunConfigControlSample.control_type` now reference it), and `ControlLayout` is gone
 * entirely — `AssayPublicGetOne.controls` is a bare `ControlSamplePublic[]` rather than an
 * object wrapping a `samples` array.
 */
export type ControlSamplePublic = Schema<'ControlSamplePublic'>;
/** @deprecated Use {@link ControlSamplePublic}. */
export type ControlSample = ControlSamplePublic;
/** @deprecated `ControlSampleType` merged into the shared {@link SampleType}. */
export type ControlSampleType = Schema<'SampleType'>;
export type AssayTarget = Schema<'AssayTarget'>;
export type AssayTargets = Schema<'AssayTargets'>;
export type AssayTargetCounts = Schema<'AssayTargetCounts'>;
export type ProteoformResultsTableColumn = Schema<'ProteoformResultsTableColumn'>;
export type ProteoformResultsTableConfig = Schema<'ProteoformResultsTableConfig'>;

/**
 * Assay type slugs, ordered for UI.
 *
 * Branch on `assay_type`, never on `assay_friendly_name` — the friendly name is free
 * text (max_length 256) with no enforced pattern, so it is display-only.
 */
export const ASSAY_TYPES: readonly AssayType[] = ['tau_proteoform', 'broadscale'] as const;

// ============================================================================
// Instrument Run Types
// ============================================================================

/**
 * InstrumentRunPublic — no longer patched.
 *
 * `instrument_name`, `started_at` and `decode_status` are in the generated schema now,
 * and `sample_qc` is gone for good: the run's QC verdict is `run_qc_outcome`, with
 * `n_pass`/`n_warn`/`n_fail`/`n_na`/`n_total` alongside it. All are non-nullable, and
 * `run_qc_outcome` is derived server-side — render it, never recompute it from the counts.
 *
 * Note `n_total` is 0 for a run with no QC rollup, which does NOT mean "no samples".
 */
export type InstrumentRunPublic = Schema<'InstrumentRunPublic'>;

/**
 * Types for creating and updating instrument run drafts.
 *
 * The API generation produced names InstrumentRunDraftCreate / InstrumentRunDraftUpdate.
 * Map our alias types to those generated schema names for compatibility with existing code.
 */
export type InstrumentRunDraftCreate = Schema<'InstrumentRunDraftCreate'>;
export type InstrumentRunDraftUpdate = Schema<'InstrumentRunDraftUpdate'>;

export type PaginatedInstrumentRunsResponse = Schema<'PaginatedInstrumentRunsResponse'>;
export type InstrumentRunSearchRequest = Schema<'InstrumentRunSearchRequest'>;
export type InstrumentRunSortField = Schema<'InstrumentRunSortField'>;
export type InstrumentRunFilters = Schema<'InstrumentRunFilters'>;

/** Renamed from `InstrumentRunSpecies`; values case-folded to 'human' | 'mouse'. */
export type Species = Schema<'Species'>;
export type InstrumentRunStatus = Schema<'InstrumentRunStatus'>;
// `SampleStatus` and `SamplePublic.status` are gone: nothing ever wrote the column and no
// screen displayed it, so the values being rendered were legacy snapshot data. PRQ-113 defines
// a real per-sample status but it will be SERVER-derived — do not recompose it client-side.
export type SampleType = Schema<'SampleType'>;

/**
 * DecodeStatus is NOT a case-fold of the old vocabulary: 'Draft' was replaced by
 * 'not_started'. Anything comparing against 'Draft' is now dead code.
 */
export type DecodeStatus = Schema<'DecodeStatus'>;

/**
 * QC outcome vocabulary, replacing the old `QCStatus`.
 *
 * Value mapping is NOT mechanical: 'Pass'->'pass', 'Fail'->'fail', but
 * 'Partial'->'warn' and 'N/A'->'not_applicable', plus a new 'not_computed' with no
 * predecessor. Every QC field carrying this type is non-nullable, so no null branch
 * is needed anywhere.
 *
 * The two non-verdict values are distinct on purpose:
 *   - `not_applicable` — determinate; the check does not apply to this sample/run.
 *   - `not_computed`   — an API-only projection meaning "no QC rollup row exists yet".
 *
 * `not_computed` is the common case in current data because QC population is
 * unfinished work — treat it as normal, never as an error state.
 */
export type QcOutcomeType = Schema<'QcOutcomeType'>;
/** @deprecated The schema was renamed `QCOutcomeType` -> `QcOutcomeType`. */
export type QCOutcomeType = QcOutcomeType;

/**
 * QC outcome values for UI set-filters, in the server's `sort_order` sequence.
 *
 * This mirrors the lookup table's current seed order for display convenience only — the
 * authoritative ordering lives server-side as data and product can reorder it with an UPDATE,
 * so never rely on these positions for sorting. Sort via `sort_by`/`sort_order` instead.
 */
/**
 * Enum value lists for AG Grid set filters.
 *
 * Enum columns filter through `{equals, not_equals}` containers only, so they must use a SET
 * filter, never a text filter — a `contains` condition is rejected with a 422. The datasource
 * already sends `logic: 'or'` for set filters, which is required: `and` plus multiple `equals`
 * on one column is contradictory and also 422s.
 */
export const INSTRUMENT_RUN_STATUSES: readonly InstrumentRunStatus[] = [
  'draft',
  'submitted',
  'started',
  'completed',
] as const;
export const DECODE_STATUSES: readonly DecodeStatus[] = [
  'not_started',
  'pending',
  'analyzing',
  'failed',
  'completed',
] as const;
export const SPECIES_VALUES: readonly Species[] = ['human', 'mouse', 'not_applicable'] as const;

/**
 * Wire value -> display label for species. The wire values are lower_snake, so they must never
 * be rendered directly. `/assays/run_configs.valid_species` is typed `Species[]` and returns
 * correctly-cased values, so feed it through here rather than transforming it.
 */
export const SPECIES_LABELS: Record<Species, string> = {
  human: 'Human',
  mouse: 'Mouse',
  not_applicable: 'N/A',
};

// Unknown/legacy wire values pass through unchanged rather than rendering as blank.
export const speciesLabel = (value: string): string =>
  value in SPECIES_LABELS ? SPECIES_LABELS[value as Species] : value;

/** Wire value -> display label for sample/control type. `AssayRunConfigControlSample.control_type`
 *  serves these lower_snake values straight from the catalog, so they must never be rendered raw. */
export const SAMPLE_TYPE_LABELS: Record<SampleType, string> = {
  customer: 'Customer',
  detection_kit_control: 'Detection Kit Control',
  library_prep_control: 'Library Prep Control',
};

/** Title-cases a lower_snake wire value. A no-op on strings with no underscore, so a display string
 *  round-trips unchanged. Consolidation target for the duplicate copies in `@/helpers/roles` and
 *  `OrgSelector/utils` — see the role-helper follow-up. */
export const titleCaseWireValue = (value: string): string =>
  value
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

// Title-cases rather than passing through, so a wire value added before `generate-api-types` reruns
// degrades to 'Some New Type' instead of leaking raw. Display strings from the deprecated
// getControlRows fallback contain no underscore and so survive unchanged.
// Own-property check, not `in`: `in` is true for inherited keys like `constructor`, which would
// return a function from a helper the callers type as string.
export const sampleTypeLabel = (value: string): string =>
  Object.hasOwn(SAMPLE_TYPE_LABELS, value) ? SAMPLE_TYPE_LABELS[value as SampleType] : titleCaseWireValue(value);

export const QC_OUTCOME_TYPES: readonly QcOutcomeType[] = [
  'pass',
  'warn',
  'fail',
  'not_applicable',
  'not_computed',
] as const;

// QCOutcomeSetFilter / WarningFilter are gone: enum and boolean columns now use the same
// { conditions, logic } container as text columns. See QcOutcomeFieldFilters /
// BooleanFieldFilters above. Note a validator 422s on `and` + multiple `equals` for one
// column, so a multi-select must send logic: 'or'.

// ============================================================================
// Sample Types
// ============================================================================

export type PaginatedSamplesResponse = Schema<'PaginatedSamplesResponse'>;
export type SampleWithNamesPublic = Schema<'SampleWithNamesPublic'>;
export type StudySample = Schema<'SampleWithNamesPublic'>;
export type OrgSampleFilters = Schema<'OrgSampleFilters'>;
export type SampleSearchRequest = Schema<'SampleSearchRequest'>;
export type SampleFilters = Schema<'SampleFilters'>;
export type SampleSortField = Schema<'SampleSortField'>;

/**
 * Derives "is this a control sample" from `sample_type`.
 *
 * `is_control` was removed from every API DTO — it is now derived rather than stored. Use this
 * for anything read from or written to the API. Note the frontend's own
 * {@link InstrumentRunDataRow} keeps an `is_control` field: that is a local form concept
 * driving the sample-manager UI, not a wire field, and is deliberately unaffected.
 */
export function isControlSample(sampleType: string | null | undefined): boolean {
  return sampleType !== null && sampleType !== undefined && sampleType !== 'customer';
}

export type SamplePublic = Schema<'SamplePublic'>;
export type Sample = Schema<'SamplePublic'>;
export type CSVParseResponse = Schema<'CSVParseResponse'>;
export type CSVSampleResponse = Schema<'CSVSampleResponse'>;

// ============================================================================
// Sample Proteoform Types
// ============================================================================

export type SampleProteoformPublic = Schema<'SampleProteoformPublic'>;
export type PaginatedSampleProteoformsResponse = Schema<'PaginatedSampleProteoformsResponse'>;
export type SampleProteoformSearchRequest = Schema<'SampleProteoformSearchRequest'>;
export type SampleProteoformFilters = Schema<'SampleProteoformFilters'>;
/**
 * Frontend-owned: `ProteoformSortField` is deliberately NOT published in the spec, because
 * `sort_by` also accepts any epitope column key from the assay's ProteoformResultsTableConfig.
 * The wire type is a plain string; anything outside these values or a valid epitope key 422s.
 * `instrument_run_name` was removed — it used to 500.
 */
export const PROTEOFORM_SORT_FIELDS = [
  'sample_name',
  'sample_group',
  'proteoform_name',
  'proteoform_abundance',
  'well_position',
  'demux_index',
  'flow_cell',
  'lane',
] as const;
export type ProteoformSortField = (typeof PROTEOFORM_SORT_FIELDS)[number];

// ============================================================================
// Proteoform Sample Summary Types
//
// Replaces the SampleProteoformCount* family, which went away with
// POST .../sample_proteoform_counts/search. The successor is
// POST .../proteoform_sample_summaries/search — one row per sample, with the three counts
// computed in a LEFT-JOINed CTE so they are sortable, filterable and pageable. The envelope
// also carries study-wide totals, which is where study-level counts now come from.
// ============================================================================

export type ProteoformSampleSummaryPublic = Schema<'ProteoformSampleSummaryPublic'>;
export type PaginatedProteoformSampleSummariesResponse = Schema<'PaginatedProteoformSampleSummariesResponse'>;
export type ProteoformSampleSummarySearchRequest = Schema<'ProteoformSampleSummarySearchRequest'>;
export type ProteoformSampleSummaryFilters = Schema<'ProteoformSampleSummaryFilters'>;
export type ProteoformSampleSummarySortField = Schema<'ProteoformSampleSummarySortField'>;
export type StudySampleProteoformSearchRequest = Schema<'StudySampleProteoformSearchRequest'>;
export type SampleQcSummary = Schema<'SampleQcSummary'>;
export type InstrumentRunSampleSize = Schema<'InstrumentRunSampleSize'>;
export type InstrumentRunConsumables = Schema<'InstrumentRunConsumables'>;
/** Status-independent PATCH body for the 7 user-entered consumable fields (see PATCH .../consumables). */
export type InstrumentRunConsumablesPatch = Schema<'InstrumentRunConsumablesPatch'>;
export type InstrumentRunConsumablesUpdateResponse = Schema<'InstrumentRunConsumablesUpdateResponse'>;

// ============================================================================

/**
 * Data row structure for instrument run sample table
 */
export type InstrumentRunDataRow = {
  name: string;
  study_name: string;
  org_name?: string;
  org_id?: string;
  well_position: string;
  demux_index: number;
  is_control?: boolean | null;
  sample_type?: string | null;
  flow_cell?: string | null;
  lane?: number | null;
  library_group?: string | null;
  sample_group?: string | null;
};

/**
 * Form values for instrument run creation form
 */
export type InstrumentRunFormValues = {
  /** FK to the assay catalog record, from GET /assays. Required on create. */
  assay_id: string;
  description?: string;
  name: string;
  noOfSamples: string;
  samples: InstrumentRunDataRow[];
  species: Species;
  libraryGroupCount?: number;
  consumables?: Partial<InstrumentRunConsumables>;
};

/**
 * Minimal structural type for a TanStack Form field render-prop argument.
 * Covers the subset of FieldApi used across InstrumentRunHeader and related components.
 * TanStack Form's actual FieldApi has 23+ generics — this avoids threading them through
 * component boundaries while still providing type safety for the values we access.
 */
export type FormFieldApi<TValue = unknown> = {
  state: { value: TValue };
  handleChange: (value: TValue) => void;
};

/**
 * Type for the instrument run form instance
 * This can be passed to child components that need access to the form API
 *
 * We use a flexible type that is compatible with TanStack Form's complex generic signature
 * while still providing type safety for the values.
 *
 * Note: We use Record<string, unknown> instead of 'any' to maintain type safety
 * while allowing additional properties from the TanStack Form API.
 */
export type InstrumentRunForm = Record<string, unknown> & {
  handleSubmit?: () => Promise<void>;
  reset?: () => void;
  setFieldValue?: (field: string, value: unknown) => void;
  state?: {
    values?: InstrumentRunFormValues;
    isSubmitting?: boolean;
    canSubmit?: boolean;
  };
  Subscribe?: ComponentType<unknown>;
  Field?: ComponentType<{ name: string; children: (field: FormFieldApi<unknown>) => ReactNode }>;
};
